import React, { useEffect, useMemo, useState } from 'react'
import T from 'prop-types'
import { Box, ListItemText, MenuItem, Stack, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CAR_CHOICE_ACCEPTED } from 'lib/carChoicesLib'
import { roundNumber } from 'lib/dateLib'

import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import InputLabel from 'components/atoms/InputLabel'
import SelectScrollable from 'components/atoms/SelectScrollable'

import { getRepriceValues } from '../../../lib/repriceFee'

const SELECT_PARTNER = 'sp'

export default function AssignToDialog({
  partners = [],
  carChoice,
  openConfirmationDialog,
  handleCloseConfirmationDialog,
  handleConfirmConfirmationDialog,
  readOnly,
}) {
  const { t } = useTranslation()

  const [localSelectValue, setLocalSelectValue] = useState(SELECT_PARTNER)
  const [localRepriceValue, setLocalRepriceValue] = useState('')
  // FIXME fee/price values
  const [localFeeValue, setLocalFeeValue] = useState('')

  const selectedPartner = useMemo(
    () => partners.find((partner) => partner.partnerId === localSelectValue),
    [localSelectValue, partners]
  )

  const partnersOptions = useMemo(
    () => [
      {
        label: t('assignment.car-details.assignToDialog.noPartner'),
        value: '',
      },
      ...partners.map(
        (partner) =>
          partner && { label: partner.partnerName, value: partner.partnerId }
      ),
    ],
    [partners, t]
  )

  const showReprice = useMemo(
    () =>
      selectedPartner &&
      (selectedPartner.reprice ||
        selectedPartner.choice !== CAR_CHOICE_ACCEPTED),
    [selectedPartner]
  )

  const handleReset = () => {
    if (localSelectValue !== SELECT_PARTNER) {
      setLocalSelectValue(SELECT_PARTNER)
    }

    if (localRepriceValue !== '') {
      setLocalRepriceValue('')
    }
    if (localFeeValue !== '') {
      setLocalFeeValue('')
    }
  }
  const handleChangeSelectLocal = (event, selected) => {
    setLocalSelectValue(selected.props.value)
  }

  const handleChangeRepriceLocal = (event) => {
    setLocalRepriceValue(event.target.value)
  }

  const handleChangeFeeLocal = (event) => {
    setLocalFeeValue(event.target.value)
  }

  const handleClose = () => {
    handleCloseConfirmationDialog()
    handleReset()
  }

  const handleConfirm = () => {
    handleConfirmConfirmationDialog(
      localSelectValue || null,
      localRepriceValue - localFeeValue,
      localFeeValue
    )
    handleReset()
  }

  useEffect(() => {
    const price =
      selectedPartner?.reprice ||
      carChoice.car[`price_group_${selectedPartner?.groupId}`] +
        carChoice.car.fee

    let repriceValues
    if (price) {
      repriceValues = getRepriceValues(price, carChoice.car.feePercentage)

      setLocalFeeValue(repriceValues.repriceFee)
    } else {
      setLocalFeeValue('')
    }

    setLocalRepriceValue(roundNumber(repriceValues?.repriceGross) || '')
  }, [
    carChoice.car,
    selectedPartner,
    selectedPartner?.groupId,
    selectedPartner?.reprice,
    showReprice,
  ])

  return (
    <ConfirmationDialog
      title={t('assignment.car-details.assignToDialog.title')}
      content={
        <>
          <Box pb={4} />

          <InputLabel label={t('common.form.partner')} />
          <SelectScrollable
            size="small"
            value={localSelectValue}
            onChange={handleChangeSelectLocal}
            disabled={readOnly}
            displayEmpty
            renderValue={(selected) => {
              return (
                partnersOptions.find((partner) => partner.value === selected)
                  ?.label ||
                t('assignment.car-details.assignToDialog.partnerPlaceholder')
              )
            }}
            fullWidth
          >
            <MenuItem disabled value={SELECT_PARTNER}>
              <ListItemText
                primary={t(
                  'assignment.car-details.assignToDialog.partnerPlaceholder'
                )}
              />
            </MenuItem>

            {partnersOptions.map((option) => {
              return (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemText primary={option.label} />
                </MenuItem>
              )
            })}
          </SelectScrollable>
          <Box pb={2} />
          {showReprice && (
            <>
              <Box pb={1}>
                <InputLabel
                  label={`${t(
                    'assignment.car-details.assignToDialog.suggestedPrice'
                  )}: € ${roundNumber(
                    carChoice?.car?.price_group_1 + carChoice?.car?.fee || 0
                  )}`}
                />
              </Box>
              {selectedPartner?.reprice && (
                <Box pb={1}>
                  <InputLabel
                    label={`${t(
                      'assignment.car-details.assignToDialog.newSuggestedPrice'
                    )}: € ${selectedPartner?.reprice}`}
                  />
                </Box>
              )}

              <Stack
                width="100%"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <InputLabel
                    label={t('assignment.car-details.assignToDialog.reprice')}
                  />
                  <TextField
                    id={`reprice.input`}
                    value={localRepriceValue || ''}
                    placeholder={t(
                      'assignment.car-details.assignToDialog.repricePlaceholder'
                    )}
                    type="number"
                    size="small"
                    variant="outlined"
                    onChange={handleChangeRepriceLocal}
                    disabled={readOnly}
                  />
                </Box>
                <Box>
                  <InputLabel
                    label={t('assignment.car-details.assignToDialog.fee')}
                  />
                  <TextField
                    id={`fee.input`}
                    value={localFeeValue || ''}
                    placeholder={t(
                      'assignment.car-details.assignToDialog.feePlaceholder'
                    )}
                    type="number"
                    size="small"
                    variant="outlined"
                    onChange={handleChangeFeeLocal}
                    disabled={readOnly}
                  />
                </Box>
              </Stack>
              <Box pb={2} />
            </>
          )}
        </>
      }
      contentComponent={Box}
      confirmLabel={t('assignment.car-details.assignToDialog.confirm')}
      open={openConfirmationDialog}
      readOnly={
        !partnersOptions.find(
          (partner) => partner.value === localSelectValue
        ) || readOnly
      }
      handleClose={handleClose}
      handleSubmit={handleConfirm}
    />
  )
}

AssignToDialog.propTypes = {
  partners: T.array,
  carChoice: T.object,
  openConfirmationDialog: T.bool,
  handleCloseConfirmationDialog: T.func,
  handleConfirmConfirmationDialog: T.func,
  readOnly: T.bool,
}
